import { BAOCustomElement } from '@bao/custom-element'
import { MediaBreakpoints } from '@bao/media'
import { debounce } from '@bao/utils'

class StickyArticles extends BAOCustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.isTicking = false
    this.lastKnownY = window.scrollY
    this.scrollDirection = ''
    this.scrollDirectionPrevious = ''

    this._stuck = false
    this.wasStuck = false

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)

    this.updateValues()
    this.updateEl()
  }

  setupListeners () {
    super.setupListeners()

    this.onScroll = this.onScroll.bind(this)
    this.listeners.add(window, 'scroll', this.onScroll)

    this.debouncedResize = debounce(() => {
      if (window.innerWidth === this.windowWidth) return

      this.updateValues()
      this.stuck = false
      this.wasStuck = false

      this.style.setProperty('--Opacity', 1)
      this.style.setProperty('--Content_Opacity', 1)
      this.els.media.element.style.insetBlockStart = '0px'
    }, 240)

    this.listeners.add(window, 'resize', this.debouncedResize)
  }

  onScroll () {
    const previouslyKnown = this.lastKnownY
    this.scrollDirectionPrevious = this.scrollDirection

    this.lastKnownY = window.scrollY
    this.scrollDirection = previouslyKnown < this.lastKnownY ? 'down' : 'up'

    this.requestTick()
  }

  updateValues () {
    this.windowWidth = window.innerWidth

    const containerHeight = this.offsetHeight
    const mediaHeight = this.els.spacer.element.offsetHeight

    // Higher number = more overhang
    this.offsetLimit = 200

    // Set maximum margin so media doesn't go over the offset limit
    this.marginLimit = containerHeight - mediaHeight - this.offsetLimit
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    if (MediaBreakpoints.isMd) return

    this.setSticky()
    this.setOpacity()
  }

  setSticky () {
    const containerRect = this.getBoundingClientRect()
    const mediaRect = this.els.media.element.getBoundingClientRect()
    const windowHeight = window.innerHeight

    const containerTop = containerRect.top
    const containerBottom = containerRect.bottom - windowHeight
    const mediaTop = mediaRect.top

    const bottomOffsetLimitReached = containerBottom <= this.offsetLimit

    if (this.scrollDirection === 'down') {
      if (
        containerTop < 0 &&
        containerBottom > 0 &&
        !this.stuck &&
        !this.wasStuck
      ) {
        this.stuck = true
        this.wasStuck = true
        return
      }

      if (bottomOffsetLimitReached && this.stuck) {
        this.stuck = false
        this.wasStuck = true

        let scrolledMargin = Math.abs(containerTop)

        if (scrolledMargin >= this.marginLimit) {
          scrolledMargin = this.marginLimit
        }

        this.els.media.element.style.insetBlockStart = `${scrolledMargin}px`
        return
      }
    }

    if (this.scrollDirection === 'up') {
      if (mediaTop > 0 && !this.stuck && this.wasStuck) {
        this.stuck = true

        this.els.media.element.style.insetBlockStart = '0px'
        return
      }

      if (containerTop > 0 && this.stuck && this.wasStuck) {
        this.stuck = false
        this.wasStuck = false

        this.els.media.element.style.insetBlockStart = '0px'
      }
    }
  }

  setOpacity () {
    const containerRect = this.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const containerTop = containerRect.top
    const containerHeight = this.offsetHeight

    const progress = Math.abs(containerTop) / (containerHeight - windowHeight)

    const minOpacity = 0.25
    const opacity = Math.max(minOpacity, 1 - Math.min(1, progress * 3))
    const contentOpacity = 1 - Math.min(1, progress * 7)

    if (this.stuck) {
      this.style.setProperty('--Opacity', opacity.toFixed(2))
      this.style.setProperty('--Content_Opacity', contentOpacity.toFixed(2))
    } else if (this.scrollDirection === 'up' && progress < 0.1) {
      this.style.setProperty('--Opacity', 1)
      this.style.setProperty('--Content_Opacity', 1)
    }
  }

  get stuck () {
    return this._stuck
  }

  set stuck (value) {
    this._stuck = value
    this.classList.toggle('sec-StickyArticles-isSticky', value)
  }
}

if (!customElements.get('sticky-articles')) {
  customElements.define('sticky-articles', StickyArticles)
}
